<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Checkbox } from '@/components/shadcn'

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'danger'    // 위험 스타일 (빨강)
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기

const props = withDefaults(
  defineProps<{
    modelValue?: string
    disabled?: boolean
    required?: boolean
    name?: string
    id?: string
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
  }>(),
  {
    modelValue: 'N',
    disabled: false,
    required: false,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
}>()

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: '' },
  primary: { 
    class: 'border-blue-500 data-[state=checked]:bg-blue-500',
  },
  success: { 
    class: 'border-green-500 data-[state=checked]:bg-green-600',
  },
  danger: { 
    class: 'border-destructive data-[state=checked]:bg-destructive',
  },
  sm: { 
    class: 'h-3.5 w-3.5',
  },
  lg: { 
    class: 'h-5 w-5',
  },
}

/** 최종 바인딩 */
const computedClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  return [preset?.class, props.class].filter(Boolean).join(' ')
})

// 내부 상태로 boolean checked 관리
const internalChecked = ref<boolean>(props.modelValue === 'Y')

// modelValue 변경 감지하여 내부 상태 동기화
watch(
  () => props.modelValue,
  (newValue) => {
    const shouldBeChecked = newValue === 'Y'
    if (internalChecked.value !== shouldBeChecked) {
      internalChecked.value = shouldBeChecked
    }
  },
  { immediate: true }
)

// 내부 상태 변경 시 modelValue로 emit
watch(internalChecked, (newChecked) => {
  const value = newChecked ? 'Y' : 'N'
  if (value !== props.modelValue) {
    emit('update:modelValue', value)
    emit('change', value)
  }
})
</script>

<template>
  <Checkbox
    :id="props.id"
    v-model="internalChecked"
    :disabled="props.disabled"
    :required="props.required"
    :name="props.name"
    :class="computedClass"
  />
</template>
